import React, { FunctionComponent, useEffect, useRef, useState, useCallback } from "react";
import dynamic from "next/dynamic";
import { useDispatch, useSelector } from "react-redux";
import { ProductImageGrid } from "@/components/responsive/product/productImageGrid/ProductImageGrid";
import { ProductMainInfos } from "@/components/responsive/product/productMainInfos/ProductMainInfos";
import { Breadcrumb } from "@/components/responsive/product/breadcrumb/Breadcrumb";
import { Pack } from "@/components/responsive/product/pack/Pack";
import { Rating } from "@/components/responsive/product/rating/Rating";
import { Questions } from "@/components/responsive/product/questions/Questions";
import { Layer } from "@/components/responsive/product/layer/Layer";
import { CategoryBlock } from "@/components/responsive/product/categoryBlock/CategoryBlock";
import { AutoPromoBanner } from "@/components/responsive/product/autoPromoBanner/AutoPromoBanner";
import { BrandContent } from "@/components/responsive/product/brandContent/BrandContent";
import { OlapicSpecificWidget } from "@/components/responsive/product/olapicSpecificWidget/OlapicSpecificWidget";
import { Target2SellBloc } from "@/components/responsive/product/blockT2S/Target2SellBloc";
import { BasketPlaceholder } from "@/components/responsive/product/basketBlock/basketPlaceholder/BasketPlaceholder";
import { Advantages } from "@/components/responsive/product/advantages/Advantages";
import Availability from "@/components/responsive/product/availability/Availability";
import { LongArrow } from "@/assets//images/responsive/svg/longArrow";
import { OfferItem } from "@/models/scope/productSheet/offer/OfferItem";
import { Product } from "@/models/scope/productSheet/Product";
import { RootState } from '@/models/appState/RootState';
import { fetchOffersData } from "@/redux/reducers/responsive/product/marketPlaceActions";
import { getTrackingData } from "redux/reducers/responsive/tracking/trackingActions";
import Utils from "@/libs/Utils";
import {
    MOBILE_TEMPLATE_VERSION,
    SOLD_BY_BUT, LAYER_TYPE,
    AVAILABLE_PRODUCT_STATUS,
    CAN_BE_AVAILABLE_STATUS,
    SOLD_BY_MKP
} from "@/utils/constants";
import { getOfferIdFromUrl, getProductPrice, getSaleabilityProduct } from "@/utils/responsive/utils";
import { initAppDataKeyValues } from "@/animations/responsive/commonPage"
import { handleClickOnContent } from "@/animations/responsive/flixMedia";
import { dispatchProductSheetLayersHandleClick } from "@/animations/responsive/productPageTracking";
import {isEligibleToScan} from "@/utils/Mobile/utils";

const DynamicBasketBlock = dynamic(
    () => import('@/components/responsive/product/basketBlock/BasketBlock'),
    { ssr: false }
);
const DynamicTopBanner = dynamic(
    () => import('@/components/responsive/product/topBanner/TopBanner'),
    { ssr: false }
);
const DynamicAdsenseBloc = dynamic(
    () => import('@/components/responsive/commun/adsence/AdsenseBloc'),
    { ssr: false }
);
const DynamicAmbassadorVideo = dynamic(
    () => import('@/components/responsive/product/ambassadorVideo/AmbassadorVideo'),
    { ssr: false }
);
const DynamicCustomizableButton = dynamic(
    () => import('@/components/responsive/product/customizableButton/CustomizableButton'),
    { ssr: false }
);

interface Props {
    productData: Product;
    templateVersion: string;
};

export const ProductPage: FunctionComponent<Props> = ({ productData, templateVersion }) => {
    const dispatch = useDispatch();
    // Redux states
    const marketplaceData = useSelector((state: RootState) => state.marketplace);
    const availability = useSelector((state: RootState) => state.availability);
    // Local states
    const [offerIdFromUrl, setOfferIdFromUrl] = useState<null | number>(null);
    const [basketBlockYPosition, setBasketBlockYPosition] = useState(0);
    const [layerType, setLayerType] = useState(LAYER_TYPE.NONE);
    const [showCloseLayerButton, setShowCloseLayerButton] = useState(false);
    const [isSelectedWarranty, setWarrantySelected] = useState(false);
    const [availablityLayerProductId, setAvailablityLayerProductId] = useState(productData.id);
    const [sellerServiceData, setSellerServiceData] = useState(marketplaceData.offersData?.items?.[0].seller);
    const [priceIncludeVAT, setPriceIncludeVAT] = useState(true);
    const [scanEligibility, setScanEligibility] = useState(false);
    // component refs
    const descriptionRef = useRef<HTMLDivElement>(null);
    // Component vars
    const isMobile = templateVersion === MOBILE_TEMPLATE_VERSION;
    const {
        soldBy, id, category, images, metaInfos, adaptiveLoadingResources, linkedPacks, packItems,
        description, features, productReviewsList, reviewQuestions, documentList,documentId, type,
        shopCircleVersion, frontLabel, markLabel, title, warranties, eligibilityToPrivateSale, unitPrice,
        labelName, configurator3DParam, configurableProduct, displayOlapicContent, adsenseStatus,
        shippingRelatedContent, stockLocatorShopsCount, imageCode, relatedContents,financingOffers, isSealableInStore
    } = productData;
    const ecoScore = (features?.find(feature => feature.group === "Caractéristiques du produit" && feature.label === "Score éco-conception")?.value);
    const isMkpAvailabilityPlaceholderShown = !marketplaceData.offersData
    // MKP
    const offersListData = marketplaceData.offersData;
    let sponsorProductWinningOffer: OfferItem[] = []
    if (marketplaceData && marketplaceData.offersData?.items) {
        sponsorProductWinningOffer = marketplaceData.offersData.items.filter(offer => offer.offerId == offerIdFromUrl);
    }
    const isFullMkp = soldBy == SOLD_BY_MKP || sponsorProductWinningOffer?.length > 0;
    // Product video infos
    const videoUrl = Utils.getMetaInfosValueByCode("videoUrl", metaInfos);
    const urlVideoAssembly = Utils.getMetaInfosValueByCode("urlVideoAssembly", metaInfos);
    const urlNotice = Utils.getMetaInfosValueByCode("noticeUrl", metaInfos);
    const videoUrl2 = Utils.getMetaInfosValueByCode("videoUrl2", metaInfos);
    const videoAmbassadorUrl = Utils.getMetaInfosValueByCode("urlVideoAmbassador", metaInfos);
    // Product bundle - pack
    const showPackBlock = linkedPacks && linkedPacks.length > 0 || packItems && packItems.length > 0;
    // Product saleability infos
    const saleability = getSaleabilityProduct(availability);
    const isBasketButtonEnable = soldBy == SOLD_BY_MKP || saleability === AVAILABLE_PRODUCT_STATUS;
    // Product price infos
    const priceInfos = getProductPrice(soldBy, productData, marketplaceData.winningOffer);
    const isEligibilityToPrivateSale = marketplaceData.winningOffer && eligibilityToPrivateSale;
    // Product advantages bloc data
    const productAdvantages = productData?.productAdvantages?.filter(advantage => advantage?.trim()?.length);

    useEffect(() => {
        soldBy !== SOLD_BY_BUT && dispatch(fetchOffersData(id));
        const offerIdFromUrl = getOfferIdFromUrl();
        offerIdFromUrl && setOfferIdFromUrl(Number(offerIdFromUrl));
        localStorage.setItem('stockLocatorShopsCount', String(stockLocatorShopsCount));
        initAppDataKeyValues(productData.navigationMetaInfosConfigs, productData.octipasPayload, productData.trackingUrlFapi,
            'productSheet', productData.mvcUrl, productData.isDQENormalisationActive, productData.shopCircleVersion, productData.logoVersion,
            null, "", "", false, templateVersion, id);
        dispatch(getTrackingData(productData.mvcUrl))
        setScanEligibility(isEligibleToScan())
    }, []);

    const handleSetLayerType = useCallback((type, sellerData = undefined) => {
        setLayerType(type);
        if (type === LAYER_TYPE.SELLER_SERVICE && sellerData) {
            setSellerServiceData(sellerData);
        }
    }, []);
    const handleCategoryBlockClick = useCallback((type) => {
        setLayerType(type);
    }, []);
    const handleClickOnDescription = () => {
        if (soldBy === SOLD_BY_MKP) {
            window.scrollTo({
                top: ((descriptionRef?.current?.offsetTop ?? 0) - (isMobile ? 0 : 97)),
                behavior: 'smooth',
            });
            handleClickOnContent();
        } else {
            setLayerType(LAYER_TYPE.DESCRIPTION);
        }
    }
    const handleOpenLayer = useCallback((type: string, selectedProductId?: string) => {
        selectedProductId && setAvailablityLayerProductId(selectedProductId);
        setLayerType(type);
    }, []);
    // Tracking infos
    let trackingData
    trackingData = {
        eventLabel: "CTA Principal",
        productVendorType: (soldBy == SOLD_BY_BUT ? "BUT" : (soldBy == SOLD_BY_MKP ? "MKP" : "BUT_MKP")),
        productVendorName: (soldBy == SOLD_BY_BUT ? "BUT" : offersListData?.items[offersListData.winningOfferIndex]?.seller?.shopName),
        productVendorId: (soldBy == SOLD_BY_BUT ? "0000" : offersListData?.items[offersListData.winningOfferIndex]?.seller?.id),
        productOfferNb: offersListData ? offersListData.items.length : 1,
        price: offersListData ? offersListData.items[offersListData.winningOfferIndex]?.price : unitPrice,
    }
    let packTrackingData = {
        ...trackingData,
        eventLabel: "Fiche_Produit"
    };

    return (
        <div className="product-content">
            <Breadcrumb category={category} />
            <div className="top">
                <div className="photo">
                    <div>
                        <ProductImageGrid images={images} isMobile={isMobile}
                            alt={productData.title}
                            videoUrl={videoUrl}
                            videoUrl2={videoUrl2}
                            adaptiveLoadingResources={adaptiveLoadingResources}
                            type={type}
                            packItems={packItems}
                            bundleImageCode={imageCode}
                            id={id}
                            documentId={documentId}
                            newEnergyClass={productData.newEnergyClass}
                            repairabilityIndex={productData.repairabilityIndex}
                            commercialOperations={productData.commercialOperations}
                            eligibilityToPrivateSale={!marketplaceData.winningOffer && productData.eligibilityToPrivateSale}
                            offersData={marketplaceData.offersData}
                            soldBy={soldBy}
                            features={features} />
                    </div>
                    {(showPackBlock && !isMobile) &&
                        <Pack linkedPacks={linkedPacks}
                            packItems={packItems}
                            isMobile={isMobile}
                            handleOpenLayer={handleOpenLayer} trackingData={packTrackingData}
                        />}
                    {!isMobile && relatedContents?.desktop && <AutoPromoBanner relatedHtmlContent={relatedContents.desktop} />}
                </div>
                <div className="data">
                    <ProductMainInfos productData={productData} priceInfos={priceInfos} winningOffer={marketplaceData.winningOffer}
                        setLayerType={handleSetLayerType} offerList={offersListData}
                        priceIncludeVAT={priceIncludeVAT}
                        setPriceIncludeVAT={setPriceIncludeVAT}
                        setShowCloseLayerButton={setShowCloseLayerButton}
                        state={offersListData?.items?.[offersListData?.winningOfferIndex].state} offersData={marketplaceData.offersData} sponsorProductWinningOffer={sponsorProductWinningOffer}
                        setWarrantySelected={setWarrantySelected}
                        isSelectedWarranty={isSelectedWarranty} />
                    {(!isFullMkp && availability.availabilityPendingStatus) || (isFullMkp && isMkpAvailabilityPlaceholderShown) ?
                        <BasketPlaceholder /> :
                        ((soldBy == SOLD_BY_MKP && marketplaceData.winningOffer) || (saleability === AVAILABLE_PRODUCT_STATUS || saleability === CAN_BE_AVAILABLE_STATUS)) &&
                        <DynamicBasketBlock
                            setBasketBlockYPosition={setBasketBlockYPosition}
                            basketBlockYPosition={basketBlockYPosition}
                            isBasketButtonEnable={isBasketButtonEnable}
                            sellerName={soldBy == SOLD_BY_MKP ? marketplaceData.winningOffer?.seller?.shopName : undefined}
                            offerId={soldBy == SOLD_BY_MKP ? marketplaceData.winningOffer?.offerId : undefined}
                            productId={id}
                            handleOpenLayer={handleOpenLayer}
                            trackingData={trackingData}
                            isSelectedWarranty={isSelectedWarranty}
                            warrantyCode={warranties?.extendedWarranties?.[0]?.parentProductId}
                            isSealableInStore={isSealableInStore}/>}

                    {isMobile &&
                        <DynamicCustomizableButton type={configurator3DParam} configurableProduct={configurableProduct} />}
                    <Availability marketplaceData={marketplaceData}
                        shopCircleVersion={shopCircleVersion} productId={id}
                        isFullMkp={(soldBy == SOLD_BY_MKP || sponsorProductWinningOffer?.length > 0)}
                        isAvailabilityPlaceholderShown={availability.availabilityPendingStatus}
                        isMkpAvailabilityPlaceholderShown={isMkpAvailabilityPlaceholderShown}
                        handleClick={handleOpenLayer}
                    />
                    {productAdvantages?.length > 0 ?
                        <Advantages productAdvantages={productAdvantages} />
                        : <div className="fp-divider-div"></div>
                    }
                    {(showPackBlock && isMobile) &&
                        <Pack linkedPacks={linkedPacks}
                            packItems={packItems}
                            isMobile={isMobile}
                            handleOpenLayer={handleOpenLayer} trackingData={packTrackingData}
                        />}
                    {description != ''
                        ? <CategoryBlock id="description" title={'Descriptif produit'} onClick={() => {
                            handleClickOnDescription();
                            dispatchProductSheetLayersHandleClick("Bloc descriptif produit", "Descriptif produit")
                        }}
                             />
                        : <></>
                    }
                    <CategoryBlock id="feature" title={'Caractéristiques'} onClick={() => {
                        handleCategoryBlockClick(LAYER_TYPE.FEATURES);
                        dispatchProductSheetLayersHandleClick("Bloc caractéristiques", "Caractéristiques")
                    }} />
                    <CategoryBlock id="review" title={'Avis client'} onClick={() => {
                        handleCategoryBlockClick(LAYER_TYPE.RATING);
                        dispatchProductSheetLayersHandleClick("Bloc avis client", "Avis client")
                    }}
                        isClickable={parseInt(productReviewsList?.reviewListStatistics.total) > 0}>
                        <Rating reviewListStatistics={productReviewsList?.reviewListStatistics}
                            underline={false} setLayerType={setLayerType} />
                    </CategoryBlock>
                    <CategoryBlock id="questions-and-answers" title={'Questions et réponses'} onClick={() => {
                        handleCategoryBlockClick(LAYER_TYPE.QUESTION);
                        dispatchProductSheetLayersHandleClick("Bloc questions et réponses", "Questions et réponses")
                    }}>
                        <span>
                            <Questions questionCount={reviewQuestions?.summary.questionsCount}
                                setLayerType={setLayerType}
                                answersCount={reviewQuestions?.summary.answersCount} />
                        </span>
                    </CategoryBlock>
                    {soldBy !== 'MKP' && ecoScore &&
                        <CategoryBlock id="eco-conception" title={'Eco-conception'} onClick={() => {
                            handleCategoryBlockClick(LAYER_TYPE.ECO_CONCEPTION);
                            dispatchProductSheetLayersHandleClick("Bloc eco-conception", "Eco-conception")
                        }}>
                            <img
                                className='eco-conception-img'
                                src={`https://media.but.fr/Pictos/Pictos-FP-V3/PICTO_NOTE-${ecoScore}_HABITONS_MIEUX.svg`}
                                alt={`Habitons mieux ${ecoScore}`}
                            />
                        </CategoryBlock>}
                    {(documentList || urlVideoAssembly || urlNotice || productData.newEnergyClass || productData.repairabilityIndex) && <CategoryBlock id="documentation" title={'Documentation produit'}
                        onClick={() => {
                            handleCategoryBlockClick(LAYER_TYPE.DOCUMENTATION);
                            dispatchProductSheetLayersHandleClick("Bloc documentation produit", "Documentation produit")
                        }} />}
                </div>
            </div>
            <div className='bottom'>
                {isMobile && relatedContents?.mobile && <AutoPromoBanner relatedHtmlContent={relatedContents.mobile} />}
                <div ref={descriptionRef}>
                    <BrandContent soldBy={soldBy} adaptiveLoadingResources={adaptiveLoadingResources} id={id} labelName={labelName} unitPrice={unitPrice} description={description} />
                </div>
                {displayOlapicContent && soldBy !== 'MKP' && <OlapicSpecificWidget addToUrl={(displayOlapicContent == 'prod') ? "?olapicForceRender" : ""} productId={id} />}
                {videoAmbassadorUrl && <DynamicAmbassadorVideo adaptiveLoadingResources={adaptiveLoadingResources} videoUrl={videoAmbassadorUrl} />}
                {!scanEligibility &&
                    <>
                        {/*Bloc T2S: recommandations */}
                        <Target2SellBloc pID="1208" espaceID="1580"/>
                        {/*Bloc T2S: Vous aimerez aussi */}
                        <Target2SellBloc pID="1208" espaceID="1581" />
                        {/*Bloc T2S: Mes derniers articles vus */}
                        <Target2SellBloc pID="1208" espaceID="1582" />
                    </>}
                {adsenseStatus &&
                    <div style={{ textAlign: 'center' }}>
                        <DynamicAdsenseBloc
                            style={{
                                "display": 'block'
                            }}
                            resources={adaptiveLoadingResources}
                            dataAdFormat="auto"
                            dataFullWidthResponsive="true"
                            dataAdClient="ca-pub-5844672657887373" dataAdSlot="5075894714"
                        />
                    </div>
                }
                <div
                    className='return-top-button'
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                >
                    <p>Retour vers le haut</p>
                    <LongArrow />
                </div>
            </div>
            {((soldBy == SOLD_BY_MKP && marketplaceData.winningOffer)

                || (saleability === AVAILABLE_PRODUCT_STATUS || saleability === CAN_BE_AVAILABLE_STATUS))
                && <DynamicTopBanner
                    warrantyCode={warranties?.extendedWarranties?.[0]?.parentProductId}
                    isSelectedWarranty={isSelectedWarranty}
                    basketBlockYPosition={basketBlockYPosition}
                    frontLabel={frontLabel}
                    title={title}
                    markLabel={markLabel}
                    type={type}
                    isEligibilityToPrivateSale={isEligibilityToPrivateSale || false}
                    privateSalePrice={productData.privateSalePrice}
                    handleOpenLayer={handleOpenLayer}
                    priceInfos={priceInfos}
                    productId={id}
                    offersListData={offersListData}
                    isBasketButtonEnable={isBasketButtonEnable}
                    soldBy={soldBy}
                />}
            <Layer
                isMobile={isMobile}
                isShow={layerType !== LAYER_TYPE.NONE}
                onClose={() => handleSetLayerType(LAYER_TYPE.NONE)}
                contentToDisplay={layerType}
                setLayerType={handleSetLayerType}
                sellerServiceData={sellerServiceData}
                description={description}
                endDateRI={productData.endDateRI}
                frontLabel={frontLabel}
                priceIncludeVAT={priceIncludeVAT}
                setPriceIncludeVAT={setPriceIncludeVAT}
                title={title}
                markLabel={markLabel}
                type={type}
                newEnergyClass={productData.newEnergyClass}
                repairabilityIndex={productData.repairabilityIndex}
                warranties={warranties?.extendedWarranties}
                mainProductId={id}
                selectedProductId={availablityLayerProductId}
                ecoScore={ecoScore}
                features={features}
                reviewListStatistics={productReviewsList?.reviewListStatistics}
                reviews={productReviewsList?.items}
                reviewQuestions={reviewQuestions}
                documentList={documentList}
                videoUrl={videoUrl}
                urlVideoAssembly={urlVideoAssembly}
                urlNotice={urlNotice}
                seller={offersListData?.items?.[0].seller}
                offerWarranty={offersListData?.items?.[0].offerWarranty}
                offerWarrantyUnit={offersListData?.items?.[0].offerWarrantyUnit}
                state={offersListData?.items?.[0].state}
                offerList={offersListData}
                soldBy={soldBy}
                shippingRelatedContent={shippingRelatedContent}
                setShowCloseLayerButton={setShowCloseLayerButton}
                showCloseLayerButton={showCloseLayerButton}
                isSelectedWarranty={isSelectedWarranty}
                setWarrantySelected={setWarrantySelected}
                saleability={saleability}
                financingOffers={financingOffers}
            />
        </div>
    )
}
